import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import apiService from "../services/api";
import Carousel from "react-bootstrap/Carousel";
import "bootstrap/dist/css/bootstrap.min.css";
import SEO from "../components/SEO";
import {
  generateOrganizationSchema,
  generateSiteNavigationSchema,
} from "../utils/seoHelpers";
import { AiOutlineArrowRight, AiOutlineArrowLeft } from "react-icons/ai";

const HomePageWrapper = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

// Carousel Styles
const CarouselSection = styled.section`
  background: white;
  width: 100%;
  margin-top: 30px;

  @media (max-width: 768px) {
    margin-top: 10px;
  }
`;

const StyledCarousel = styled(Carousel)`
  width: 100%;
  overflow: hidden;
  background: white;
  height: 100vh;

  .carousel-item {
    height: auto;
    position: relative;
  }

  .carousel-indicators {
    bottom: 20px;
    z-index: 3;

    button {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      border: 2px solid white;
      background-color: rgba(255, 255, 255, 0.5);

      &.active {
        background-color: #ff6720;
        border-color: #ff6720;
      }
    }
  }

  /* Custom navigation arrows styling */
  .carousel-control-prev,
  .carousel-control-next {
    width: 50px;
    height: 50px;
    background-color: rgba(255, 103, 32, 0.8);
    border-radius: 50%;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0.8;
    transition: all 0.3s ease;

    &:hover {
      background-color: rgba(255, 103, 32, 1);
      opacity: 1;
    }

    .carousel-control-prev-icon,
    .carousel-control-next-icon {
      width: 20px;
      height: 20px;
    }
  }

  .carousel-control-prev {
    left: 20px;
  }

  .carousel-control-next {
    right: 20px;
  }

  @media (max-width: 768px) {
    height: fit-content;

    .carousel-control-prev,
    .carousel-control-next {
      width: 30px;
      height: 30px;
      top: auto;
      bottom: 15px;
      transform: none;
      left: auto;

      .carousel-control-prev-icon,
      .carousel-control-next-icon {
        width: 16px;
        height: 16px;
      }
    }

    .carousel-control-prev {
      right: 70px;
    }

    .carousel-control-next {
      right: 20px;
    }

    .carousel-indicators {
      button {
        width: 8px; /* Reduced size for responsive */
        height: 8px; /* Reduced size for responsive */
        margin-bottom: -50px; /* Move indicators below controls */
      }
    }
  }
`;

const CarouselSlide = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
`;

// Text section at the top
const SlideTextSection = styled.div`
  background: #ffffffff;
  text-align: left;
  padding-bottom: 20px;

  @media (max-width: 768px) {
    padding: 20px 0;
    text-align: center;
  }
`;

const SlideTextContent = styled.div`
  max-width: 1300px;
  display: flex;
  margin: 0 auto;
  padding: 0 20px;
  height: fit-content !important;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0 30px;
    align-items: start;
    text-align: start;
    margin-bottom: 0px;
  }

  @media (max-width: 480px) {
    padding: 0 20px;
  }
`;

const SlideTagline = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 15px;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 0px;
  }
`;

const SlideTitle = styled.h2`
  font-size: 48px;
  font-weight: 900;
  margin-bottom: 25px;
  line-height: 1.2;
  color: #333;
  max-width: 800px;

  @media (max-width: 1024px) {
    font-size: 40px;
  }

  @media (max-width: 768px) {
    font-size: 32px;
    max-width: none;
  }

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

// Buttons for the text section (top)
const SlideTextButtons = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
    margin-top: -15px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const SlideTextPrimaryButton = styled(Link)`
  display: inline-block;
  background: #ff6720;
  color: white;
  width: fit-content;
  height: fit-content;
  padding: 12px 28px;
  border-radius: 50px;
  font-weight: bold;
  fontfamily: roboto, sans-serif;
  font-size: 14px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 103, 32, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: #e55a1c;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(255, 103, 32, 0.4);
    color: white;
  }

  @media (max-width: 480px) {
    padding: 10px 30px;
    font-size: 15px;
    width: auto;
    text-align: center;
  }
`;

// Image section below with buttons positioned at left bottom
const SlideImageSection = styled.div`
  height: 100vh;
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;

  ${(props) =>
    props.mediaType === "image" &&
    props.bgImage &&
    `
    background-image: url(${props.bgImage});
  `}

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) => props.bgGradient || "rgba(0, 0, 0, 0)"};
    z-index: 1;
  }

  /* Video styles */
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
  }

  @media (max-width: 768px) {
    height: 400px;
  }

  @media (max-width: 480px) {
    height: 300px;
  }
`;

// Icon Row Section below carousel
const IconRowSection = styled.section`
  background: #f2f2f2;
  padding: 40px 20px;
  display: flex;
  justify-content: center;
  gap: 60px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 40px;
    padding: 30px 20px;
  }

  @media (max-width: 480px) {
    gap: 30px;
    flex-direction: column;
    padding: 25px 20px;
  }
`;

const IconInSection = styled.div`
  display: flex;
  flex-warp: wrap;
  justify-content: space-between;
  max-width: 1300px;
  width: 100%;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }
`;

const HeadingIn = styled.div`
  font-size: 18px;
  font-weight: 500;
  width: 200px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const IconItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 480px) {
    gap: 15px;
  }
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid #33333385;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }
`;

const IconLabel = styled.span`
  font-weight: 600;
  color: #333;
  font-size: 16px;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const ShopByCategorySection = styled.section`
  background: #ffffff;
  padding: 30px 0;
  overflow-x: hidden; /* Prevent horizontal overflow */

  @media (max-width: 768px) {
    padding: 50px 0;
  }

  @media (max-width: 480px) {
    padding: 40px 0;
  }
`;

const CategoryContainer = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  overflow: hidden; /* Add this to contain any overflow */
`;

const CategoryTitle1 = styled.h2`
  font-size: 48px;
  font-weight: bold;
  color: #333;
  margin-top: 20px;
  margin-bottom: 10px;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 40px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
    margin-bottom: 30px;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden; /* Add this to prevent overflow */
  width: 100%; /* Ensure it doesn't exceed parent width */
`;

const CategoriesScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-behavior: auto;
  gap: 20px;
  padding: 20px 0;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  /* Prevent container from extending beyond parent */
  max-width: 100%;
  width: 100%;

  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const CategoryCardBox = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: transparent;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #5c5c5c83;
  width: 250px;
  border-radius: 10px;
  justify-content: space-between; /* This ensures content spreads out */
  height: 230px;
  padding: 20px 20px 60px; /* Consistent padding on all sides */
  overflow: hidden;
  flex-shrink: 0;
  position: relative; /* Add this for better control */

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    background: #ffffff;
    color: inherit;
    border-color: transparent;
  }

  @media (max-width: 768px) {
    min-width: 180px;
    padding: 15px 15px 40px; /* Adjusted mobile padding */
  }

  @media (max-width: 480px) {
    width: 150px;
    height: 150px;
    border: 1px solid #5c5c5c83;
    padding: 10px 10px 30px; /* Consistent mobile padding */
  }
`;

const CategoryIcon = styled.div`
  width: 100%;
  height: 140px; /* Fixed height for consistency */
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0; /* Remove margin since we're using absolute positioning */

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 480px) {
    height: 80px; /* Adjusted for mobile */
  }
`;

const CategoryName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
  line-height: 1.4;
  text-align: center;
  width: 100%;
  position: absolute; /* This ensures it stays at bottom */
  bottom: 20px; /* Position from bottom */
  left: 0;
  right: 0;
  padding: 0 10px;

  @media (max-width: 480px) {
    font-size: 14px;
    bottom: 15px; /* Adjusted for mobile */
  }
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #333;
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  transition: all 0.3s ease;

  &:hover {
    background: #ff6720;
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: #333;
      transform: translateY(-50%) scale(1);
    }
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const LeftArrow = styled(ArrowButton)`
  left: 15px;
  opacity: ${(props) => (props.show ? 1 : 0)};
  visibility: ${(props) => (props.show ? "visible" : "hidden")};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    left: 10px;
  }
`;

const RightArrow = styled(ArrowButton)`
  right: 15px;
  opacity: ${(props) => (props.show ? 1 : 0)};
  visibility: ${(props) => (props.show ? "visible" : "hidden")};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    right: 10px;
  }
`;

const ProgressBarContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 15px;
    margin-top: 30px;
    padding: 0 20px;
  }

  @media (max-width: 480px) {
    margin-top: 0px;
    padding: 0px;
  }
`;

const ProgressBar = styled.div`
  position: relative;
  width: 100%;
  height: 2px;
  background-color: #e0e0e0;
  border-radius: 2px;
`;

const ProgressFill = styled.div`
  position: absolute;
  height: 100%;
  width: 30px;
  background-color: #333;
  border-radius: 2px;
  transition: left 0.3s ease;
  left: ${(props) => `calc((100% - 30px) * ${props.progress / 100})`};
`;

const ArrowButtonMobile = styled.button`
  width: 36px;
  height: 36px;
  background: transparent;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 18px;
  font-weight: 300;

  &:hover:not(:disabled) {
    color: #333;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const ImageInSection = styled.section`
  width: 100%;
  height: 800px; /* FIXED HEIGHT - No viewport units */
  position: relative;
  overflow: hidden;

  .container {
    height: 100%;
    position: relative;
    max-width: none;
    width: 100%;
    padding: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: bottom;
    }
  }

  /* Keep same height across ALL breakpoints */
  @media (max-width: 1024px) {
    height: 600px; /* Same height */
  }

  @media (max-width: 768px) {
    height: 600px; /* Same height */
  }

  @media (max-width: 480px) {
    height: 400px; /* Only reduce for very small screens */
  }
`;

const ProductHotspot = styled.div`
  position: absolute;
  width: ${(props) => (props.isMobile ? "16px" : "20px")};
  height: ${(props) => (props.isMobile ? "16px" : "20px")};
  background: rgba(255, 255, 255, 0.9);
  border: 3px solid #ff6720;
  border-radius: 50%;
  cursor: pointer;
  z-index: 1000;
  animation: pulse 1.5s infinite;
  will-change: left, top;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${(props) => (props.isMobile ? "6px" : "8px")};
    height: ${(props) => (props.isMobile ? "6px" : "8px")};
    background: #ff6720;
    border-radius: 50%;
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(255, 255, 255, 1);
    }
    70% {
      box-shadow: 0 0 0 ${(props) => (props.isMobile ? "8px" : "10px")}
        rgba(255, 103, 32, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(255, 103, 32, 0);
    }
  }
`;

const ProductPopup = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  min-width: 250px;
  margin-bottom: 10px;
  z-index: 10;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 8px solid transparent;
    border-top-color: white;
  }
`;

const PopupImage = styled.img`
  width: 90px !important;
  height: 90px !important;
  object-fit: contain;
  border-radius: 8px;
  margin-right: 15px;
`;

const PopupContent = styled.div`
  display: flex;
  align-items: center;
`;

const PopupDetails = styled.div`
  flex: 1;
`;

const PopupTitle = styled.h4`
  font-size: 14px;
  font-weight: bold;
  margin: 0 0 4px 0;
  color: #333;
`;

const PopupButton = styled(Link)`
  background: #ff6720;
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  text-decoration: none;
  font-size: 12px;
  font-weight: bold;
  display: inline-block;

  &:hover {
    background: #e55a1c;
    color: white;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 36px;
  font-weight: bold;
  color: #333;
  margin-bottom: 50px;

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const SubcategorySection = styled.section`
  background: white;
  padding: 80px o;
  margin-top: 30px;
  margin-bottom: 80px;

  @media (max-width: 768px) {
    padding: 50px 0;
  }

  .containerSubcategories {
    max-width: 1300px;
    margin: 0 auto;
  }
`;

const SubcategoryGrid = styled.div`
  display: flex;
  flex-direction: row;
  gap: 30px;
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const SubcategoryCard = styled(Link)`
  position: relative;
  height: auto; /* Reduced height for horizontal layout */
  overflow: hidden;
  text-decoration: none;
  display: flex;
  flex-direction: column; /* Change to horizontal layout */
  align-items: center;
  transition: transform 0.3s ease;
  background: #f5f5f5;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    height: 200px;
    flex-direction: column;
  }

  @media (max-width: 480px) {
    height: 180px;
  }
`;

const SubcategoryImage = styled.div`
  width: 90%; /* Take 40% width for image */
  height: 400px;
  align-self: start;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-bottom-right-radius: 100px;
  }

  &::after {
    content: "";
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 20px;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 60%;
  }
`;

const SubcategoryContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 30px;
  background: transparent;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const SubcategoryTitle = styled.h3`
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 22px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const SubcategoryCount = styled.span`
  display: inline-block;
  padding: 8px 20px;
  background: #333;
  color: white;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  width: fit-content;
`;

const HotspotProductsSection = styled.section`
  max-width: 1150px;
  background: white;
  padding: 60px 20px;
  margin-top: -100px !important;
  border-radius: 8px;
  margin: 0 auto;
  position: relative;

  @media (max-width: 768px) {
    padding: 20px 0;
    align-items: start;
    margin-top: 10px !important;
  }
`;

const HotspotProductsContainer = styled.div`
  margin: 0 auto;
  padding: 0 20px;
  background: white;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  @media (max-width: 768px) {
    padding: 0 10px;
    gap: 3px;
    align-items: start;
  }
`;

const HotspotProductsContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  @media (max-width: 968px) {
    align-items: center;
    text-align: center;
  }

  @media (max-width: 768px) {
    align-items: flex-start;
    text-align: start;
    height: fit-content;
  }
`;

const HotspotProductsTitle = styled.h2`
  font-size: 18px;
  font-weight: 400;
  line-height: 1.2;
  color: #333;
  margin: 0 0 40px 0;
  text-align: center;

  @media (max-width: 1200px) {
    font-size: 42px;
  }

  @media (max-width: 968px) {
    font-size: 36px;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    text-align: start;
    line-height: auto;
    font-weight: 600;
  }
`;

const HotspotProductsCarouselWrapper = styled.div`
  position: relative;
  overflow: hidden;

  @media (max-width: 968px) {
    max-width: 600px;
    margin: 0 auto;
  }
`;

const HotspotProductsCarousel = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

const HotspotProductCard = styled(Link)`
  position: relative;
  background: white;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: all 0.4s ease;
  border: 1px solid #f0f0f0;
  width: 150px;
  height: 120px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);

    .hotspot-product-image {
      opacity: 0;
    }

    .hotspot-product-info {
      opacity: 1;
    }
  }
`;

const HotspotProductImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #ffffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.4s ease;
  opacity: 1;
  transform: scale(1);

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    max-width: 150px;
  }
`;

const HotspotProductInfo = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 20px;
  text-align: center;
  transition: all 0.4s ease;
  opacity: 0;
  transform: scale(1.1);
`;

const HotspotProductName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 5px 0;
  line-height: 1.2;
`;

const HotspotProductFullName = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 10px 0;
  line-height: 1.3;
`;

const ScrollBarContainer = styled.div`
  width: 100%;
  padding: 15px 0 5px 0;
  display: flex;
  justify-content: center;
`;

const ScrollBarTrack = styled.div`
  position: relative;
  width: 100%;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  cursor: pointer;
  transition: background 0.2s ease;
  overflow: hidden;

  &:hover {
    background: #d0d0d0;
  }

  /* Loading animation */
  ${props => !props.ready && `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 50%, #e0e0e0 75%);
      background-size: 200% 100%;
      animation: loading 1.5s infinite;
    }
    
    @keyframes loading {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `}
`;

const ScrollBarThumb = styled.div`
  position: absolute;
  height: 100%;
  background: #ff6720;
  border-radius: 3px;
  transition: background 0.2s ease;
  cursor: grab;
  user-select: none;
  min-width: 20px; /* Minimum thumb width */

  &:hover {
    background: #e55a1c;
  }

  &:active {
    cursor: grabbing;
    background: #d45319;
  }
`;

const MiddleRevealSection = styled.section`
  background: white;
  max-width: 1300px;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  padding: 30px 0;

  @media (max-width: 768px) {
    padding: 80px 20px;
  }

  @media (max-width: 480px) {
    padding: 60px 20px;
  }
`;

const MiddleContainer = styled.div`
  margin: 0 auto;
  display: flex;
  flex-direction: row;
  padding: 0 20px;
  gap: 40px;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }
`;

const MiddleContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  height: fit-content;
  width: 100%;
  border-bottom-right-radius: 100px;

  @media (max-width: 968px) {
    align-items: center;
    text-align: center;
  }
`;

const MiddleTagline = styled.p`
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 10px;
  color: #666;

  @media (max-width: 768px) {
    font-size: 14px;
    letter-spacing: 1px;
  }
`;

const MiddleTitle = styled.h2`
  font-size: 72px;
  font-weight: 900;
  margin-bottom: 20px;
  line-height: 1.1;
  color: #333;

  @media (max-width: 1024px) {
    font-size: 60px;
  }

  @media (max-width: 768px) {
    font-size: 48px;
  }

  @media (max-width: 480px) {
    font-size: 36px;
  }
`;

const MiddleSubtitle = styled.p`
  font-size: 18px;
  font-weight: 400;
  margin-bottom: 30px;
  line-height: 1.6;
  word-spacing: .05em;
  letter-spacing: 0.05em;
  color: #666;

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const MiddleButton = styled(Link)`
  display: inline-block;
  background: #ff6720;
  color: white;
  padding: 16px 40px;
  border-radius: 50px;
  font-weight: bold;
  font-size: 16px;
  text-decoration: none;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 15px rgba(255, 103, 32, 0.3);

  &:hover {
    background: #e55a1c;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(255, 103, 32, 0.4);
    color: white;
  }

  @media (max-width: 480px) {
    padding: 14px 32px;
    font-size: 14px;
  }
`;

const MiddleImage = styled.div`
  position: relative;
  width: 60%;
  height: 600px;
  border-bottom-right-radius: 100px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 968px) {
    height: 400px;
    order: -1;
  }

  @media (max-width: 480px) {
    height: 300px;
  }
`;

const MachineRevealContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MachinePart = styled.div`
  position: absolute;
  width: ${props => props.customWidth || (props.isMobile ? "280px" : "250px")};
  height: ${props => props.customHeight || "auto"};
  transition: all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  z-index: 3;
  will-change: transform, opacity;
  
  /* Different entrance animations */
  ${props => {
    switch(props.animationType) {
      case 'slideFromLeft':
        return `
          transform: translate(-50%, -50%) translateX(-100px) scale(0.8);
          opacity: 0;
          &.visible {
            transform: translate(-50%, -50%) translateX(0) scale(1);
            opacity: 1;
          }
        `;
      case 'slideFromRight':
        return `
          transform: translate(-50%, -50%) translateX(100px) scale(0.8);
          opacity: 0;
          &.visible {
            transform: translate(-50%, -50%) translateX(0) scale(1);
            opacity: 1;
          }
        `;
      default:
        return `
          transform: translate(-50%, -50%) scale(0.6);
          opacity: 0;
          &.visible {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
        `;
    }
  }}
  
   transition-delay: ${props => props.delay || '0s'};
  
  img {
    width: 100%;
    height: 100%;
    display: block;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
    object-fit: ${props => props.objectFit || "contain"};
  }

  @media (max-width: 768px) {
    width: ${props => props.customWidth || (props.isMobile ? "50px" : "70px")};
  }

  @media (max-width: 480px) {
    width: ${props => props.customWidth || (props.isMobile ? "40px" : "60px")};
  }
`;

// second parallax section
const ParallaxSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  margin-top: 80px;
  margin-bottom: 80px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-attachment: fixed;  /* This makes the background stick */
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: url('https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/bg12.jpg');

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }

  @media (max-width: 768px) {
    background-attachment: fixed;  /* Falls back to scroll on mobile for better performance */
    min-height: 60vh;
  }
`;

const ParallaxContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  padding: 60px 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const ParallaxTitle = styled.h2`
  font-size: 72px;
  font-weight: 900;
  margin-bottom: 30px;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.5);

  @media (max-width: 1024px) {
    font-size: 56px;
  }

  @media (max-width: 768px) {
    font-size: 42px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const ParallaxSubtitle = styled.p`
  font-size: 24px;
  font-weight: 400;
  line-height: 1.6;
  margin-bottom: 40px;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.5);
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const ParallaxButton = styled(Link)`
  display: inline-block;
  background: #ff6720;
  color: white;
  padding: 18px 45px;
  border-radius: 50px;
  font-weight: bold;
  font-size: 16px;
  text-decoration: none;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 15px rgba(255, 103, 32, 0.4);

  &:hover {
    background: #e55a1c;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(255, 103, 32, 0.6);
    color: white;
  }

  @media (max-width: 480px) {
    padding: 14px 32px;
    font-size: 14px;
  }
`;

// const BottomSection = styled.section`
//   width: 100%;
//   height: auto;

//   img {
//     width: 100%;
//     height: auto;
//   } 
// `;

// Machine parts data for the first slide
const machineParts = [
  {
    id: 'power-weeder-new',
    image: "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/STIHlMH210Powerweeder.png",
    animationType: 'slideFromRight',
    delay: '0.2s',
    desktop: { x: "60%", y: "44%" },
    mobile: { x: "65%", y: "25%" },
    // Add custom dimensions
    width: "340px",  // Custom width
    height: "auto",  // Custom height
    objectFit: "contain" // Optional: cover, contain, fill, etc.
  },
  {
    id: 'ms-363-chainsaw',
    image: "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/STIHLMS363Chainsaw.png",
    animationType: 'slideFromLeft', 
    delay: '0.2s',
    desktop: { x: "47.1%", y: "53.9%" },
    mobile: { x: "20%", y: "50%" },
    width: "400px",
    height: "150px"
  },
  {
    id: 'ehc-705-s',
    image: "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/ehc-705-s.png",
    animationType: 'slideFromLeft', 
    delay: '0.2s',
    desktop: { x: "70%", y: "57.6%" },
    mobile: { x: "20%", y: "50%" },
    width: "200px",
    height: "auto"
  },
];

// Carousel data
const carouselData = [
  {
    id: 1,
    tagline: "Professional Tools Made Easy",
    title: "Your personal workshop companion",
    description: "Discover premium collection of professional-grade tools designed for demanding projects and everyday tasks.",
    primaryButtonText: "Shop Professional Tools",
    primaryButtonLink: "/collections/professional",
    secondaryButtonText: "How To Use", 
    secondaryButtonLink: "/about",
    mediaType: "image", // Changed from "video" to "machine-reveal"
    bgImage:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/bg12.jpg",
    // backgroundImage: "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/heroBG1.png",
    // machineParts: machineParts
  },
  {
    id: 2,
    tagline: "Outdoor Solutions Made Simple",
    title: "Transform your outdoor space",
    description:
      "Complete range of garden tools and equipment to maintain and beautify your outdoor environment with ease.",
    primaryButtonText: "Explore Garden Tools",
    primaryButtonLink: "/collections/garden",
    secondaryButtonText: "Learn More",
    secondaryButtonLink: "/collections/garden",
    mediaType: "video", // Second slide is PHOTO
    videoUrl:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/battery.mp4",
  },
  {
    id: 3,
    tagline: "Limited Time Offers",
    title: "Incredible deals on quality tools",
    description:
      "Save big on selected premium tools and equipment. Professional quality at unbeatable prices for a limited time.",
    primaryButtonText: "View All Deals",
    primaryButtonLink: "/collections/deals",
    secondaryButtonText: "Compare Products",
    secondaryButtonLink: "/compare",
    mediaType: "image", // Third slide is PHOTO
    bgImage:
      "https://www.stihl.in/content/dam/stihl/media/pim/9917.jpg.transform/StihlImageGallery-mq4/img.jpg",
  },
];

const shopCategoryData = [
  {
    id: "all-products",
    name: "All Products",
    image: "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/Allproduct.jpg", // Use your desired image
    link: "/collections/all-categories",
  },
  {
    id: "chainsaws",
    name: "Chainsaws",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/MS382Chainsaw.jpg",
    link: "/collections/chainsaws",
  },
  {
    id: "brushchutters",
    name: "Brushchutters",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/FS250Brushcutter.jpg",
    link: "/collections/blowers",
  },
  {
    id: "hedge-trimmers",
    name: "Hedge Trimmers",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/HSE52HedgeTrimmer.jpg",
    link: "/collections/hand-tools",
  },
  {
    id: "pole-pruners",
    name: "Pole Pruners",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/HTA86PolePruners.jpg",
    link: "/collections/outdoor-equipment",
  },
  {
    id: "blowers",
    name: "Blowers",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/BGA86BlowerBattery.jpg",
    link: "/collections/cordless-stick-vacuums",
  },
  {
    id: "kombi-multi-system",
    name: "KombiSystem & MultiSystem",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/KM85RKombiengine.jpg",
    link: "/collections/gardening",
  },
  {
    id: "power-weeders",
    name: "Power Weeders",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/MH710PowerWeeder.jpg",
    link: "/collections/handheld-vacuums",
  },
  {
    id: "lawn-mower",
    name: "Lawn Mower",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/RME235ElectricLawnMower.jpg",
    link: "/collections/replacement-filters",
  },
  {
    id: "mulching-lawn-mowers",
    name: "Mulching lawn mowers",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/Mulchinglawnmowers.jpg",
    link: "/collections/storage",
  },
  {
    id: "mistblowers",
    name: "Mistblowers",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/SR420Mistblower.jpg",
    link: "/collections/storage",
  },
  {
    id: "spayer",
    name: "Sprayer",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/SG230Sprayer.jpg",
    link: "/collections/storage",
  },
  {
    id: "water-pump",
    name: "Water Pump",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/WP300WaterPump.jpg",
    link: "/collections/storage",
  },
  {
    id: "vaccum-cleaners",
    name: "Vaccum Cleaners",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/SE62VaccumCleaner.jpg",
    link: "/collections/storage",
  },
  {
    id: "pressure-washers",
    name: "Pressue Washers",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/RE150ElectricPressureWasher.jpg",
    link: "/collections/storage",
  },
  {
    id: "earth-augers",
    name: "Earth Augers",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/BT131EarthAuger.jpg",
    link: "/collections/storage",
  },
  {
    id: "sweeping-machines",
    name: "Sweeping Machines",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/KG770SweepingMachines.jpg",
    link: "/collections/storage",
  },
  {
    id: "engines",
    name: "Engines",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/EHC705SEngines.jpg",
    link: "/collections/storage",
  },
  {
    id: "compressor",
    name: "Compressor",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/KOA20Compressor.jpg",
    link: "/collections/storage",
  },
  {
    id: "cutt-off-machines",
    name: "Cutt-Off Machines",
    image:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/TSA230Cutt-offMachines.jpg",
    link: "/collections/storage",
  },
];

const responsiveImageHotspots = [
  {
    id: "chainsaw",
    desktop: { x: "70%", y: "50%" }, // Adjusted for desktop image layout
    mobile: { x: "50%", y: "80%" }, // Adjusted for mobile image layout
    product: {
      name: "MS 363 Chainsaw",
      image:
        "https://www.stihl.co.za/content/dam/stihl/media/pim/117454.jpg.transform/StihlImageGallery-mq4/img.jpg",
      link: "/products/ms-271-chainsaw",
    },
  },
  {
    id: "power-weeder",
    desktop: { x: "50%", y: "50%" }, // Adjusted for desktop image layout
    mobile: { x: "50%", y: "80%" }, // Adjusted for mobile image layout
    product: {
      name: "MH 210 - 4 Stroke",
      image:
        "https://www.stihl.co.za/content/dam/stihl/media/pim/117454.jpg.transform/StihlImageGallery-mq4/img.jpg",
      link: "/products/ms-271-chainsaw",
    },
  },
  {
    id: "pruners",
    desktop: { x: "24%", y: "53%" }, // Adjusted for desktop image layout
    mobile: { x: "50%", y: "80%" }, // Adjusted for mobile image layout
    product: {
      name: "GTA 40 Pruner",
      image:
        "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/GTA40Pruner.jpg",
      link: "/products/ms-271-chainsaw",
    },
  },
  {
    id: "pruning-shear",
    desktop: { x: "37%", y: "56%" }, // Adjusted for desktop image layout
    mobile: { x: "50%", y: "80%" }, // Adjusted for mobile image layout
    product: {
      name: "ASA 20",
      image:
        "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/ASA20LoppingShears.jpg",
      link: "/products/ms-271-chainsaw",
    },
  },
  {
    id: "pressure-washer",
    desktop: { x: "16%", y: "78%" },
    mobile: { x: "15%", y: "75%" },
    product: {
      name: "REA 60 Plus Pressure Washer",
      image: "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/REA60PlusPressureWasher.jpg",
      link: "/products/br-600-blower",
    },
  },
  {
    id: "compressor",
    desktop: { x: "40%", y: "78%" },
    mobile: { x: "35%", y: "65%" },
    product: {
      name: "KOA 20 Compressor",
      image:
        "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/KOA20Compressor.jpg",
      link: "/products/fs-131-trimmer",
    },
  },
  {
    id: "pressure-washer-2",
    desktop: { x: "80%", y: "80%" },
    mobile: { x: "80%", y: "75%" },
    product: {
      name: "RCA 20 Pressure Washer",
      image:
        "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/RCA20PressureWasher.jpg",
      link: "/products/rma-510-mower",
    },
  },
  {
    id: "stationary-engine",
    desktop: { x: "70%", y: "80%" },
    mobile: { x: "80%", y: "75%" },
    product: {
      name: "EHC 1505 S",
      image:
        "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/EHC705SEngines.jpg",
      link: "/products/rma-510-mower",
    },
  },
];

const categoryData = [
  {
    id: "chainsaws",
    title: "Chainsaws",
    description:
      "Professional petrol, electric, and battery chainsaws for every cutting task and application.",
    image:
      "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop",
    link: "/collections/chainsaws",
    bgColor: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
    accentColor: "#991b1b",
  },
  {
    id: "brushcutters",
    title: "Brushcutters",
    description:
      "Heavy-duty brushcutters and trimmers for maintaining large areas and tough vegetation.",
    image:
      "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=300&fit=crop",
    link: "/collections/brushcutters",
    bgColor: "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)",
    accentColor: "#16a34a",
  },
  {
    id: "blowers",
    title: "Leaf Blowers",
    description:
      "Powerful petrol, electric, and battery blowers for efficient debris clearing.",
    image:
      "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop",
    link: "/collections/blowers",
    bgColor: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
    accentColor: "#0e7490",
  },
  {
    id: "pressure-washers",
    title: "Pressure Washers",
    description:
      "High-pressure cleaning equipment for professional and domestic cleaning applications.",
    image:
      "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop",
    link: "/collections/pressure-washers",
    bgColor: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    accentColor: "#1d4ed8",
  },
  {
    id: "lawn-mowers",
    title: "Lawn Mowers",
    description:
      "Self-propelled, push, and ride-on mowers in petrol, electric, and battery options.",
    image:
      "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop",
    link: "/collections/lawn-mowers",
    bgColor: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    accentColor: "#047857",
  },
];

const HomePage = () => {
  const [featuredProductsData, setFeaturedProductsData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [subcategoriesData, setSubcategoriesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [isResizing, setIsResizing] = useState(false);
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSrc, setImageSrc] = useState(
    window.innerWidth <= 480
      ? "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/4mobilescaled.png"
      : "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/16ratioscaled.png",
  );
  
  // Add these states for machine reveal animation
  const [visibleParts, setVisibleParts] = useState([]);
  const [carouselActiveIndex, setCarouselActiveIndex] = useState(0);

  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [hotspotPositions, setHotspotPositions] = useState([]);
  const imageRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const hotspotCarouselRef = useRef(null);
  const scrollBarRef = useRef(null);
  const [scrollThumbWidth, setScrollThumbWidth] = useState(25);
  const [scrollThumbPosition, setScrollThumbPosition] = useState(0);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startScrollLeft = useRef(0);
  const [carouselReady, setCarouselReady] = useState(false);

  const [machinePartPositions, setMachinePartPositions] = useState([]);
  const machineContainerRef = useRef(null);
  const [machineImageLoaded, setMachineImageLoaded] = useState(false);
  const [productSubcategories, setProductSubcategories] = useState([]);

  useEffect(() => {
    // Only run the machine reveal for the first slide (index 0)
    if (carouselActiveIndex === 0) {
      setVisibleParts([]);
      
      const timer = setTimeout(() => {
        machineParts.forEach((part, index) => {
          setTimeout(() => {
            setVisibleParts(prev => [...prev, part.id]);
          }, index * 300 + 500);
        });
      }, 500);

      return () => clearTimeout(timer);
    } else {
      // Reset when not on first slide
      setVisibleParts([]);
    }
  }, [carouselActiveIndex]); // Only depend on carouselActiveIndex

  // Handle carousel slide change
  const handleCarouselSelect = (selectedIndex) => {
    setCarouselActiveIndex(selectedIndex);
  };

  // Add to your existing useEffect for resize
  useEffect(() => {
    calculateMachinePartPositions();
    window.addEventListener("resize", calculateMachinePartPositions);
    return () => window.removeEventListener("resize", calculateMachinePartPositions);
  }, [machineImageLoaded, isMobile, windowWidth]);

  // Update your main resize effect to include machine positions
  useEffect(() => {
    let resizeTimeout;
    const checkScreenSize = () => {
      setIsResizing(true);
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const width = window.innerWidth;
        setWindowWidth(width);
        setIsMobile(width <= 480);
        
        // Recalculate both hotspot and machine positions
        calculateHotspotPositions();
        calculateMachinePartPositions();
        
        setIsResizing(false);
      }, 500);
    };
    
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => {
      window.removeEventListener("resize", checkScreenSize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [productsResponse, categoriesResponse, subcategoriesResponse, productSubcategoriesResponse] = await Promise.all([
          apiService.getFeaturedProducts(3),
          apiService.getCategories(),
          apiService.getSubcategories(),
          apiService.getProductSubcategories(), // ADD THIS LINE
        ]);
        if (productsResponse.success) {
          setFeaturedProductsData(productsResponse.data || []);
        }
        if (categoriesResponse.success) {
          setCategoriesData(categoriesResponse.data || []);
        }
        if (subcategoriesResponse.success) {
          setSubcategoriesData(subcategoriesResponse.data || []);
        }
        // ADD THIS BLOCK
        if (productSubcategoriesResponse.success) {
          setProductSubcategories(productSubcategoriesResponse.data || []);
        }
      } catch (err) {
        console.error("Failed to load homepage data:", err);
        setError(err.message);
        setCategoriesData(categoryData);
        setFeaturedProductsData([
          {
            id: "fallback-1",
            name: "Professional Power Tools",
            short_description: "High-quality tools for every project",
            images: [
              {
                image_url:
                  "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=160&h=120&fit=crop",
                alt_text: "Power Tools",
              },
            ],
            slug: "power-tools",
          },
        ]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const getSubcategoryProductCount = (subcategoryId) => {
    if (!productSubcategories.length) return 0;
    
    const productIdsWithSubcategory = productSubcategories
      .filter(ps => ps.subcategory_id === subcategoryId)
      .map(ps => ps.product_id);
    
    return new Set(productIdsWithSubcategory).size; // Use Set to avoid counting duplicates
  };

  useEffect(() => {
    const debugSubcategories = async () => {
      try {
        const response = await apiService.debugSubcategoryProducts();
        console.log('=== SUBCATEGORY DEBUG ===');
        console.log('Products with subcategory:', response.data);
      } catch (error) {
        console.error('Debug error:', error);
      }
    };
    
    if (subcategoriesData.length > 0) {
      debugSubcategories();
    }
  }, [subcategoriesData]);

  useEffect(() => {
    // Preload images
    const mobileImage = new Image();
    mobileImage.src =
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/image2.png";
    const desktopImage = new Image();
    desktopImage.src =
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/image2.png";
  }, []);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);

      const maxScroll = scrollWidth - clientWidth;
      const progress = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
      setScrollProgress(progress);
    }
  };

  // Improved updateScrollBar function
  const updateScrollBar = () => {
    if (!hotspotCarouselRef.current || !scrollBarRef.current) return;
    
    const carousel = hotspotCarouselRef.current;
    const track = scrollBarRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = carousel;
    const trackWidth = track.getBoundingClientRect().width;
    
    // Force a reflow to ensure accurate measurements
    void carousel.offsetHeight;
    
    const maxScroll = scrollWidth - clientWidth;
    
    if (maxScroll > 0 && scrollWidth > 0 && trackWidth > 0) {
      // Calculate thumb width as percentage of track width
      const visibleRatio = clientWidth / scrollWidth;
      const thumbWidth = Math.max(visibleRatio * trackWidth, 20); // Minimum 20px
      
      // Calculate thumb position
      const scrollRatio = scrollLeft / maxScroll;
      const thumbPosition = scrollRatio * (trackWidth - thumbWidth);
      
      setScrollThumbWidth(thumbWidth);
      setScrollThumbPosition(thumbPosition);
    } else {
      // If no scroll needed, thumb takes full width
      setScrollThumbWidth(trackWidth);
      setScrollThumbPosition(0);
    }
  };

  // Initialize scrollbar after component mounts and images load
  useEffect(() => {
    const initializeScrollbar = () => {
      // Multiple attempts to ensure proper initialization
      const attempts = [0, 100, 300, 500];
      
      attempts.forEach(delay => {
        setTimeout(() => {
          updateScrollBar();
          setCarouselReady(true);
        }, delay);
      });
    };

    // Initialize when component mounts
    initializeScrollbar();

    // Also initialize when window loads
    window.addEventListener('load', initializeScrollbar);

    // Update on resize with debounce
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateScrollBar, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('load', initializeScrollbar);
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  // Update scrollbar when carousel scrolls
  useEffect(() => {
    const carousel = hotspotCarouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', updateScrollBar);
      return () => carousel.removeEventListener('scroll', updateScrollBar);
    }
  }, []);

  // Force update when hotspot data changes
  // Force update when hotspot data changes and when images load
  useEffect(() => {
    if (responsiveImageHotspots.length > 0) {
      // Wait for images to load
      const carousel = hotspotCarouselRef.current;
      if (carousel) {
        const images = carousel.querySelectorAll('img');
        let loadedCount = 0;
        
        const checkAllLoaded = () => {
          loadedCount++;
          if (loadedCount === images.length) {
            setTimeout(updateScrollBar, 100);
            setTimeout(updateScrollBar, 300);
          }
        };
        
        images.forEach(img => {
          if (img.complete) {
            checkAllLoaded();
          } else {
            img.addEventListener('load', checkAllLoaded);
          }
        });
      }
      
      // Fallback updates
      setTimeout(updateScrollBar, 200);
      setTimeout(updateScrollBar, 500);
    }
  }, [responsiveImageHotspots.length]);

  // Improved scrollbar click handler
  const handleScrollBarClick = (e) => {
    if (!scrollBarRef.current || !hotspotCarouselRef.current) return;
    
    const track = scrollBarRef.current;
    const carousel = hotspotCarouselRef.current;
    const trackRect = track.getBoundingClientRect();
    
    const clickX = e.clientX - trackRect.left;
    const trackWidth = trackRect.width;
    
    const { scrollWidth, clientWidth } = carousel;
    const maxScroll = scrollWidth - clientWidth;
    
    if (maxScroll > 0) {
      // Calculate click position as percentage of track
      const clickPercent = Math.min(Math.max(clickX / trackWidth, 0), 1);
      const targetScroll = clickPercent * maxScroll;
      
      carousel.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  };

  // Improved thumb drag handler
  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    isDragging.current = true;
    startX.current = e.clientX;
    startScrollLeft.current = hotspotCarouselRef.current?.scrollLeft || 0;
    
    const handleMouseMove = (moveEvent) => {
      if (!isDragging.current || !hotspotCarouselRef.current || !scrollBarRef.current) return;
      
      const carousel = hotspotCarouselRef.current;
      const track = scrollBarRef.current;
      const trackRect = track.getBoundingClientRect();
      const trackWidth = trackRect.width;
      
      const deltaX = moveEvent.clientX - startX.current;
      const { scrollWidth, clientWidth } = carousel;
      const maxScroll = scrollWidth - clientWidth;
      
      if (maxScroll > 0) {
        // Calculate how much to scroll based on drag distance
        const scrollPerPixel = maxScroll / (trackWidth - scrollThumbWidth);
        const scrollDelta = deltaX * scrollPerPixel;
        
        const newScrollLeft = Math.max(0, Math.min(maxScroll, startScrollLeft.current + scrollDelta));
        carousel.scrollLeft = newScrollLeft;
      }
    };
    
    const handleMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Initialize and update scrollbar on resize and load
  useEffect(() => {
    const updateOnResize = () => {
      setTimeout(updateScrollBar, 150);
    };

    // Initial update
    updateScrollBar();
    
    // Update when window resizes
    window.addEventListener('resize', updateOnResize);
    
    // Update when carousel content loads
    const carousel = hotspotCarouselRef.current;
    if (carousel) {
      carousel.addEventListener('load', updateScrollBar, true);
    }

    return () => {
      window.removeEventListener('resize', updateOnResize);
      if (carousel) {
        carousel.removeEventListener('load', updateScrollBar, true);
      }
    };
  }, []);

  // Add carousel drag functionality
  useEffect(() => {
    const carousel = hotspotCarouselRef.current;
    if (!carousel) return;

    let isCarouselDragging = false;
    let startCarouselX = 0;
    let startScrollLeft = 0;

    const handleMouseDown = (e) => {
      isCarouselDragging = true;
      startCarouselX = e.pageX - carousel.offsetLeft;
      startScrollLeft = carousel.scrollLeft;
      carousel.style.cursor = 'grabbing';
      carousel.style.userSelect = 'none';
    };

    const handleMouseMove = (e) => {
      if (!isCarouselDragging) return;
      e.preventDefault();
      const x = e.pageX - carousel.offsetLeft;
      const walk = (x - startCarouselX) * 2; // Scroll-fast
      carousel.scrollLeft = startScrollLeft - walk;
    };

    const handleMouseUp = () => {
      isCarouselDragging = false;
      carousel.style.cursor = 'grab';
      carousel.style.removeProperty('user-select');
    };

    carousel.addEventListener('mousedown', handleMouseDown);
    carousel.addEventListener('mousemove', handleMouseMove);
    carousel.addEventListener('mouseup', handleMouseUp);
    carousel.addEventListener('mouseleave', handleMouseUp);

    return () => {
      carousel.removeEventListener('mousedown', handleMouseDown);
      carousel.removeEventListener('mousemove', handleMouseMove);
      carousel.removeEventListener('mouseup', handleMouseUp);
      carousel.removeEventListener('mouseleave', handleMouseUp);
    };
  }, []);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -1000, behavior: "instant" });
      // Update the scroll state immediately
      setTimeout(() => {
        if (scrollRef.current) {
          const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
          setShowLeftArrow(scrollLeft > 0);
          setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
      }, 10);
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 1000, behavior: "instant" });
      // Update the scroll state immediately
      setTimeout(() => {
        if (scrollRef.current) {
          const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
          setShowLeftArrow(scrollLeft > 0);
          setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
      }, 10);
    }
  };

  const calculateMachinePartPositions = () => {
    const container = machineContainerRef.current;
    if (!container || !machineImageLoaded) return;

    const bgImage = container.querySelector('.machine-bg-image');
    if (!bgImage) return;

    const cw = bgImage.clientWidth;
    const ch = bgImage.clientHeight;
    const nw = bgImage.naturalWidth;
    const nh = bgImage.naturalHeight;

    if (!nw || !nh) return;

    // Same calculation as ProductHotspot
    const s = Math.max(cw / nw, ch / nh); // Scale factor for cover
    const dw = nw * s; // Displayed width
    const dh = nh * s; // Displayed height

    const leftOffset = (cw - dw) / 2; // Horizontal center offset
    const topOffset = (ch - dh) / 2; // Vertical center offset

    const positions = machineParts.map((part) => {
      const pos = isMobile ? part.mobile : part.desktop;
      const imageX = (parseFloat(pos.x) / 100) * nw;
      const imageY = (parseFloat(pos.y) / 100) * nh;

      const dx = imageX * s + leftOffset;
      const dy = imageY * s + topOffset;

      const cx = (dx / cw) * 100;
      const cy = (dy / ch) * 100;

      return {
        x: `${Math.min(Math.max(cx, 0), 100)}%`,
        y: `${Math.min(Math.max(cy, 0), 100)}%`,
      };
    });

    setMachinePartPositions(positions);
  };

  //new one
  const calculateHotspotPositions = () => {
    const img = imageRef.current;
    if (!img || !imageLoaded) return;

    const cw = img.clientWidth;
    const ch = img.clientHeight;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;

    if (!nw || !nh) return;

    const s = Math.max(cw / nw, ch / nh); // Scale factor for cover
    const dw = nw * s; // Displayed width
    const dh = nh * s; // Displayed height

    const leftOffset = (cw - dw) / 2; // Horizontal center offset
    const topOffset = ch - dh; // Vertical bottom offset

    const positions = responsiveImageHotspots.map((hotspot) => {
      const pos = isMobile ? hotspot.mobile : hotspot.desktop;
      const imageX = (parseFloat(pos.x) / 100) * nw;
      const imageY = (parseFloat(pos.y) / 100) * nh;

      const dx = imageX * s + leftOffset;
      const dy = imageY * s + topOffset;

      const cx = (dx / cw) * 100;
      const cy = (dy / ch) * 100;

      // Clamp to 0-100% to prevent off-screen positions
      return {
        x: `${Math.min(Math.max(cx, 0), 100)}%`,
        y: `${Math.min(Math.max(cy, 0), 100)}%`,
      };
    });

    setHotspotPositions(positions);
  };

  useEffect(() => {
    calculateHotspotPositions();
    window.addEventListener("resize", calculateHotspotPositions);
    return () =>
      window.removeEventListener("resize", calculateHotspotPositions);
  }, [imageLoaded, isMobile, windowWidth]);

  useEffect(() => {
    const checkInitialScroll = () => {
      if (scrollRef.current) {
        handleScroll();
      }
    };
    setTimeout(checkInitialScroll, 100);
  }, []);

  useEffect(() => {
    let resizeTimeout;
    const checkScreenSize = () => {
      setIsResizing(true);
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const width = window.innerWidth;
        setWindowWidth(width);
        setIsMobile(width <= 480);
        setImageSrc(
          width <= 480
            ? "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/image2.png"
            : "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/image2.png",
        );
        setIsResizing(false);
      }, 500);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => {
      window.removeEventListener("resize", checkScreenSize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  if (loading) {
    return (
      <HomePageWrapper>
        <div style={{ padding: "100px 20px", textAlign: "center" }}>
          <h2>Loading...</h2>
          <p>Fetching the latest products and categories...</p>
        </div>
      </HomePageWrapper>
    );
  }

  return (
    <HomePageWrapper>
      <SEO
        title="Home | XPLOD-GROUPS - Professional Power Tools & Garden Equipment Kerala"
        description="Shop professional power tools, chainsaws, blowers, lawn mowers, and garden equipment in Kerala. Authorized STIHL dealer with expert service and support."
        keywords="power tools Kerala, STIHL Kerala, chainsaws, blowers, garden tools, professional tools, lawn mowers, Pathanamthitta, authorized dealer"
        canonical="/"
        structuredData={[
          generateOrganizationSchema(),
          generateSiteNavigationSchema(),
        ]}
      />
      {/* Carousel Section */}
      <CarouselSection>
        <div
          className="container"
          style={{ maxWidth: "none", width: "100%", padding: "0" }}
        >
          <StyledCarousel
            interval={5000}
            controls={true}
            indicators
            pause="hover"
            wrap={true}
            onSelect={handleCarouselSelect} // Add this line
            activeIndex={carouselActiveIndex} // Add this line
          >
            {carouselData.map((slide) => (
              <Carousel.Item key={slide.id}>
                <CarouselSlide>
                  {/* Text section at the top with buttons */}
                  <SlideTextSection>
                    <SlideTextContent>
                      <div>
                        <SlideTagline>{slide.tagline}</SlideTagline>
                        <SlideTitle>{slide.title}</SlideTitle>
                      </div>
                      <SlideTextButtons>
                        <SlideTextPrimaryButton to={slide.primaryButtonLink}>
                          {slide.primaryButtonText}
                        </SlideTextPrimaryButton>
                      </SlideTextButtons>
                    </SlideTextContent>
                  </SlideTextSection>

                  {/* Media section - Updated for machine reveal */}
                  <SlideImageSection
                    mediaType={slide.mediaType}
                    bgImage={slide.mediaType !== "machine-reveal" ? slide.bgImage : slide.backgroundImage}
                  >
                    {slide.mediaType === "video" && slide.videoUrl && (
                      <video autoPlay loop muted playsInline poster={slide.posterImage}>
                        <source src={slide.videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    )}
                    
                    {slide.mediaType === "image" && !slide.videoUrl && (
                      /* Static image for other slides */
                      <div style={{
                        width: '100%',
                        height: '100%',
                        backgroundImage: `url(${slide.bgImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }} />
                    )}
                    
                    {slide.mediaType === "machine-reveal" && (
                      <SlideImageSection
                        mediaType={slide.mediaType}
                        ref={machineContainerRef}
                      >
                        {/* Make the background image VISIBLE and use object-fit: cover */}
                        <img
                          className="machine-bg-image"
                          src={slide.backgroundImage}
                          alt="STIHL Products"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "bottom",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            zIndex: 0 // Behind the machine parts
                          }}
                          onLoad={() => {
                            setMachineImageLoaded(true);
                            calculateMachinePartPositions();
                          }}
                          onError={() => setMachineImageLoaded(true)}
                        />
                        
                        <MachineRevealContainer>
                          {slide.machineParts.map((part, index) => {
                            const position = machinePartPositions[index] || { x: "50%", y: "50%" };
                            return (
                              <MachinePart
                                key={part.id}
                                animationType={part.animationType}
                                delay={part.delay}
                                isMobile={isMobile}
                                customWidth={part.width}
                                customHeight={part.height}
                                objectFit={part.objectFit}
                                className={visibleParts.includes(part.id) ? 'visible' : ''}
                                style={{
                                  left: position.x,
                                  top: position.y,
                                  zIndex: 2,
                                  transition: isResizing ? "none" : "left 0.3s ease, top 0.3s ease, transform 1.2s ease, opacity 1.2s ease",
                                }}
                              >
                                <img src={part.image} alt={`${part.id} part`} />
                              </MachinePart>
                            );
                          })}
                        </MachineRevealContainer>
                      </SlideImageSection>
                    )}
                  </SlideImageSection>
                </CarouselSlide>
              </Carousel.Item>
            ))}
          </StyledCarousel>
        </div>
      </CarouselSection>

      <IconRowSection>
        <IconInSection>
          <HeadingIn>Kerala, India's leading STIHL dealer since 2005</HeadingIn>
          <IconItem>
            <IconCircle>
              {/* Bundle + Save Icon - Replace with your own */}
              <svg width="30" height="25" viewBox="0 0 30 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.72665 3.86784L4.68723 10.4855C3.77892 13.4309 5.43273 15.607 8.35904 15.607H17.9135C18.0103 15.6097 18.1051 15.635 18.1902 15.6809C18.2753 15.7267 18.3484 15.7918 18.4035 15.8708C18.4585 15.9499 18.4941 16.0406 18.5072 16.1358C18.5203 16.231 18.5107 16.328 18.479 16.4188L17.9649 18.0849C17.8799 18.3047 17.7353 18.4969 17.547 18.6402C17.3587 18.7835 17.1341 18.8722 16.898 18.8967H1.88089L0 25H19.3659C21.5081 25 23.3248 24.0182 24.2588 20.9878L26.1954 14.7017C27.1123 11.7265 25.407 9.6014 22.7378 9.6014H13.1362C12.605 9.6014 12.395 9.21888 12.5407 8.75136L13.0548 7.08527C13.1171 6.80352 13.2757 6.55183 13.5037 6.37287C13.7318 6.19392 14.0152 6.09873 14.3059 6.10346H28.1191L30 0.000129266H11.6667C9.04884 -0.0168717 7.41645 1.64497 6.72665 3.86784Z" fill="#F37A1F"/>
              </svg>
            </IconCircle>
            <IconLabel style={{ fontWeight: "600"}}>OFFICIAL STIHL DEALER</IconLabel>
          </IconItem>

          <IconItem>
            <IconCircle>
              {/* Buy Now Pay Later Icon - Replace with your own */}
              <img src="https://www.stihl.in/content/dam/stihl/system-assets/icons/dealer-locator/IN-Platinum-Plus-Badge.png" alt="" style={{ width: "30px", height: "auto"}}></img>
            </IconCircle>
            <IconLabel style={{ fontWeight: "600"}}>Platinum Dealer</IconLabel>
          </IconItem>

          <IconItem>
            <IconCircle>
              {/* Buy Now Pay Later Icon - Replace with your own */}
              <img src="https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/plan.png" alt="EMI options, monthly payments" style={{ width: "30px", height: "auto"}}></img>
            </IconCircle>
            <IconLabel style={{ fontWeight: "600"}}>EMI + SMAM Subsidy</IconLabel>
          </IconItem>

          <IconItem>
            <IconCircle>
              {/* Buy Now Pay Later Icon - Replace with your own */}
              <img src="https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/mechanic_12744012.png" alt="Expert Technicians" style={{ width: "30px", height: "auto"}}></img>
            </IconCircle>
            <IconLabel style={{ fontWeight: "600"}}>Expert Technicians</IconLabel>
          </IconItem>
        </IconInSection>
      </IconRowSection>

      {/* Shop by Category Carousel Section */}
      <ShopByCategorySection>
        <CategoryContainer>
          <CategoryTitle1>Shop by category</CategoryTitle1>
          <CarouselWrapper>
            <LeftArrow show={showLeftArrow} onClick={scrollLeft}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </LeftArrow>

            <CategoriesScrollContainer ref={scrollRef} onScroll={handleScroll}>
              {shopCategoryData.map((category) => (
                <CategoryCardBox key={category.id} to={category.link}>
                  <CategoryIcon>
                    <img src={category.image} alt={category.name} />
                  </CategoryIcon>
                  <CategoryName>{category.name}</CategoryName>
                </CategoryCardBox>
              ))}
            </CategoriesScrollContainer>

            <RightArrow show={showRightArrow} onClick={scrollRight}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </RightArrow>
          </CarouselWrapper>

          {/* Mobile Progress Bar with Arrows */}
          <ProgressBarContainer>
            <ProgressBar>
              <ProgressFill progress={scrollProgress} />
            </ProgressBar>

            <ArrowButtonMobile onClick={scrollLeft} disabled={!showLeftArrow}>
              <AiOutlineArrowLeft />
            </ArrowButtonMobile>

            <ArrowButtonMobile onClick={scrollRight} disabled={!showRightArrow}>
              <AiOutlineArrowRight />
            </ArrowButtonMobile>
          </ProgressBarContainer>
        </CategoryContainer>
      </ShopByCategorySection>

      <ImageInSection>
        <div className="container">
          <img
            ref={imageRef}
            src={imageSrc}
            alt="STIHL Products"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "bottom",
            }}
            onLoad={() => {
              setImageLoaded(true);
              calculateHotspotPositions(); // Initial calculation after load
            }}
            onError={() => setImageLoaded(true)}
          />
          {imageLoaded &&
            responsiveImageHotspots.map((hotspot, index) => {
              const position = hotspotPositions[index] || {
                x: "50%",
                y: "50%",
              }; // Fallback if not calculated yet
              return (
                <ProductHotspot
                  key={hotspot.id}
                  style={{
                    left: position.x,
                    top: position.y,
                    transition: isResizing
                      ? "none"
                      : "left 0.3s ease, top 0.3s ease",
                  }}
                  isMobile={isMobile}
                  onClick={() =>
                    setActiveHotspot(
                      activeHotspot === hotspot.id ? null : hotspot.id,
                    )
                  }
                  data-hotspot
                >
                  {activeHotspot === hotspot.id && (
                    <ProductPopup>
                      <PopupContent>
                        <PopupImage
                          src={hotspot.product.image}
                          alt={hotspot.product.name}
                        />
                        <PopupDetails>
                          <PopupTitle>{hotspot.product.name}</PopupTitle>
                          <PopupButton to={hotspot.product.link}>
                            View Product
                          </PopupButton>
                        </PopupDetails>
                      </PopupContent>
                    </ProductPopup>
                  )}
                </ProductHotspot>
              );
            })}
        </div>
      </ImageInSection>
      {/* Hotspot Products Section - Split Layout */}
      <HotspotProductsSection>
        <HotspotProductsContainer>
          <HotspotProductsContent>
            <HotspotProductsTitle>
              Easily keep your yard<br />
              clean with the right<br />
              outdoor tools.
            </HotspotProductsTitle>
          </HotspotProductsContent>

          {/* Right Side - Products Carousel */}
          <HotspotProductsCarouselWrapper>
            <HotspotProductsCarousel 
              ref={hotspotCarouselRef}
              onScroll={updateScrollBar}
            >
              {responsiveImageHotspots.map((hotspot) => (
                <HotspotProductCard key={hotspot.id} to={hotspot.product.link}>
                  <HotspotProductImage className="hotspot-product-image">
                    <img src={hotspot.product.image} alt={hotspot.product.name} />
                  </HotspotProductImage>
                  <HotspotProductInfo className="hotspot-product-info">
                    <HotspotProductName>
                      {hotspot.product.name.split(' ')[0]} {hotspot.product.name.split(' ')[1]}
                    </HotspotProductName>
                    <HotspotProductFullName>{hotspot.product.name}</HotspotProductFullName>
                  </HotspotProductInfo>
                </HotspotProductCard>
              ))}
            </HotspotProductsCarousel>
            
            {/* Simple Scroll Bar */}
            <ScrollBarContainer>
              <ScrollBarTrack 
                ref={scrollBarRef}
                onClick={handleScrollBarClick}
                ready={carouselReady}
              >
                {carouselReady && (
                  <ScrollBarThumb 
                    style={{ 
                      width: `${scrollThumbWidth}px`,
                      left: `${scrollThumbPosition}px`
                    }}
                    onMouseDown={handleThumbMouseDown}
                  />
                )}
              </ScrollBarTrack>
            </ScrollBarContainer>
          </HotspotProductsCarouselWrapper>
        </HotspotProductsContainer>
      </HotspotProductsSection>

      <MiddleRevealSection>
        <MiddleContainer>
          <MiddleContent>
            <MiddleTagline>SMAM Subsidy</MiddleTagline>
            <MiddleTitle>Supporting Farmers with SMAM Subsidies</MiddleTitle>
            <MiddleSubtitle>We are committed to making advanced agricultural tools accessible through India’s Sub-Mission on Agricultural Mechanization (SMAM) subsidy program. As a proud partner, we help farmers access STIHL’s world-class power tools at subsidized rates, enhancing productivity and sustainability. Our team guides you through the SMAM application process, ensuring seamless access to subsidies for equipment like chainsaws, brush cutters, and more. With 14 branches across Kerala, we’re here to empower farmers with the tools and support they need to thrive.</MiddleSubtitle>
            <MiddleButton to="/about">
              know more
            </MiddleButton>
          </MiddleContent>
          
          <MiddleImage>
            <img 
              src="https://www.stihl.in/content/dam/stihl/vu/in/en/images/external/Tiller_Farming_range.png.transform/contentteaser-mq4/img.png" 
              alt="Middle Reveal" 
            />
          </MiddleImage>
        </MiddleContainer>
      </MiddleRevealSection>

      <ParallaxSection>
        <ParallaxContent>
          <ParallaxTitle>For The Brave</ParallaxTitle>
          <ParallaxSubtitle>
            Built for professionals who face the toughest challenges. 
            Our equipment stands ready when duty calls.
          </ParallaxSubtitle>
          <ParallaxButton to="/collections/professional">
            Explore Professional Tools
          </ParallaxButton>
        </ParallaxContent>
      </ParallaxSection>

      {/* Subcategory Section - Custom IDs */}
      <SubcategorySection>
        <div className="containerSubcategories">
          <SectionTitle>We're passionate about your projects</SectionTitle>
          
          {(() => {
            // Define which subcategory IDs you want to show and their custom data
            const subcategoryConfig = {
              5: {
                title: "Fall Yard Cleaning Essentials",
                image: "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/gardening.jpg"
              },
              2: {
                title: "Your Go-To Cleaning Products", 
                image: "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/pressurewashing.jpg"
              },
              6: {
                title: "Your Go-To Cleaning Products", 
                image: "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/ehcengine.jpg"
              }
            };
            
            const selectedSubcategoryIds = [5, 2, 6];

            const selectedSubcategories = selectedSubcategoryIds
              .map(id => {
                const subcategory = subcategoriesData.find(sub => sub.id === id);
                const config = subcategoryConfig[id];
                
                if (!subcategory) return null;
                
                const productCount = getSubcategoryProductCount(id);
                
                return { 
                  ...subcategory, 
                  ...config,
                  displayCount: productCount
                };
              })
              .filter(Boolean);

            console.log('Final subcategories with counts:', selectedSubcategories);

            if (selectedSubcategories.length === 0) {
              return (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                  <h3>No subcategories found</h3>
                  <p>The selected subcategory IDs ({selectedSubcategoryIds.join(', ')}) were not found.</p>
                  <p style={{ fontSize: '14px', marginTop: '10px' }}>
                    Available IDs: {subcategoriesData.map(s => s.id).join(', ') || 'None'}
                  </p>
                </div>
              );
            }
            
            return (
              <>
                <SubcategoryGrid>
                  {selectedSubcategories.map((subcategory) => (
                    <SubcategoryCard 
                      key={subcategory.id}
                      to={`/collections/subcategory?subcategory_id=${subcategory.id}`}
                      bgColor={subcategory.bgColor}
                    >
                      <SubcategoryImage>
                        <img 
                          src={subcategory.image || subcategory.image_url} 
                          alt={subcategory.title || subcategory.name}
                        />
                      </SubcategoryImage>
                      
                      <SubcategoryContent>
                        <SubcategoryTitle>
                          {subcategory.title || subcategory.name}
                        </SubcategoryTitle>
                        <SubcategoryCount>
                          {subcategory.displayCount} products
                        </SubcategoryCount>
                      </SubcategoryContent>
                    </SubcategoryCard>
                  ))}
                </SubcategoryGrid>
              </>
            );
          })()}
        </div>
      </SubcategorySection>
      {/* <BottomSection>
        <img src="https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/mapping.png" alt="mapping"></img>
      </BottomSection> */}
    </HomePageWrapper>
  );
};

export default HomePage;
